import { t } from 'i18next';
import '../../../styles/counselingGrid.css';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'react-modal';
import InputText from '../../common/inputs/InputText';
import { closeCounselingChannelModal, openCounselingCounselorModal } from '../../../helpers/h-react-modals';
import Button from '../../common/buttons/Button';
import NoImageUserAvatar from '../../../assets/image/profile_photo.png';
import { RootState } from '../../../redux/store';
import InputRadioCheckbox from '../../common/inputs/InputRadioCheckbox';
import { GridApi } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { ICounselorInfo } from '../../../interfaces/api/I-api-hrs-users';
import { AgHeaderTextCenter } from '../../common/AgCustomHeader';
import { toast } from 'react-toastify';
import {
  setChannelReloadStatus,
  setSelectedUsers,
  setSelectedChannel,
} from '../../../redux/slice/counselingManagementSlice';
import {
  IAdminCounselCounselorReq,
  IChannel,
  IChannelv2,
} from '../../../interfaces/api/counsel/I-api-admin-counsel-category';
import { reqAddChannel, reqAddCounselor, reqModifyChannel, reqRemoveCounselor } from '../../../helpers/h-counselingMng';
import { counselCounselorListApi } from '../../../api/admin/counseling/api-admin-counsel-counselor';
import { IAdminCounselCounselorRes } from '../../../interfaces/api/counsel/I-api-admin-counsel-counselor';
import { useAppSelector } from '../../../redux/hooks';
import {
  presignedImageRequest,
  presignedURLIssue,
  storageFilePost,
  storageFileSearch,
} from '../../../api/admin/api-common-storage';
import { getMyTenantId } from '../../../helpers/h-userInfo';
import CounselingChannelsProfile from '../../mainPage/counseling-management/CounselingChannelsProfile';
import CounselingRoleSelectbox from './CounselingRoleSelectbox';
import CounselingTextBox from './CounselingTextBox';
import { filterNewUsers, getExistingData } from './utils';

const customStyles = {
  overlay: {
    zIndex: 99,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    minWidth: '41.875rem',
    padding: 0,
  },
};

interface Props {
  isOpen: boolean;
}

const CounselingChannelModal = ({ isOpen }: Props) => {
  const dispatch = useDispatch();
  const subTitle = useRef<HTMLHeadingElement | null>(null);
  const { selectedCategoryId, selectedCategoryName, selectedChannel } = useSelector(
    (state: RootState) => state.counselingManagement,
  );
  const [channelName, setChannelName] = useState('');
  const [introduceDesc, setIntroduceDesc] = useState('');
  const [fixedStatus, setFixedStatus] = useState<number>(0);
  const [gridApi, setGridApi] = useState<GridApi<ICounselorInfo>>();
  const [profilePicUrl, setProfilePicUrl] = useState(NoImageUserAvatar);

  const gridRef = useRef<AgGridReact<ICounselorInfo>>(null);
  const { selectedUsers } = useSelector((state: RootState) => state.counselingManagement);
  const [localUserData, setLocalUserData] = useState<Array<ICounselorInfo>>(selectedUsers.map((user) => ({ ...user })));

  const { usersid } = useAppSelector((state) => state.user);

  const inputHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setChannelName(event.target.value);
  };

  const inputIntroduceHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setIntroduceDesc(event.target.value);
  };

  const handleClear = () => {
    setChannelName('');
  };

  const handleIntroduceClear = () => {
    setIntroduceDesc('');
  };

  const handleDelete = () => {
    if (!gridApi) return;
    const selectedRows = gridApi.getSelectedRows();

    if (selectedRows.length === 0) {
      toast.error(t('CounselingManagement.channelModalDelMsg') as string);
      return;
    }

    const updatedUserData = localUserData.filter(
      (user) => !selectedRows.some((selectedUser) => selectedUser.userid === user.userid),
    );

    setLocalUserData(updatedUserData);
    dispatch(setSelectedUsers(updatedUserData));
    gridApi.setRowData(updatedUserData);
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: ' ',
        field: 'checkbox',
        checkboxSelection: true,
        maxWidth: 50,
        resizable: false,
      },
      {
        headerName: t('CounselingManagement.channelHeaderName') as string,
        field: 'username',
        editable: false,
        headerComponent: AgHeaderTextCenter,
        cellStyle: { textAlign: 'center' },
      },
      {
        headerName: t('CounselingManagement.channelHeaderNickname') as string,
        field: 'nickname',
        editable: true,
        cellEditor: 'agTextCellEditor',
        // cellStyle: { textAlign: 'center' },
        onCellValueChanged: (params: any) => {
          const updatedData = localUserData.map((user) =>
            user.userid === params.data.userid ? { ...user, nickname: params.newValue } : { ...user },
          );
          setLocalUserData(updatedData);
          dispatch(setSelectedUsers(updatedData));
        },
      },
      {
        headerName: t('CounselingManagement.channelHeaderNickname'),
        field: 'role',
        editable: true,
        cellEditor: CounselingRoleSelectbox,
        cellRenderer: CounselingTextBox,
      },
    ],
    [localUserData],
  );

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      resizable: false,
      sortable: false,
    }),
    [],
  );

  const onGridReady = useCallback(
    (params: { api: GridApi<ICounselorInfo> }) => {
      setGridApi(params.api);
      gridRef.current?.api.setRowData(localUserData);
    },
    [localUserData],
  );

  // useEffect(() => {
  //   const initialData = selectedUsers.map((user) => ({ ...user }));
  //   setLocalUserData(initialData);
  //   if (gridApi) {
  //     gridApi.setRowData(initialData);
  //   }
  // }, [gridApi]);

  const onCellValueChanged = (params: any) => {
    setLocalUserData((prev) =>
      prev.map((row) => (row.bid === params.data.bid ? { ...row, role: params.data.role } : row)),
    );
  };

  useEffect(() => {
    if (!gridApi) return;

    const existingData: ICounselorInfo[] = getExistingData(gridApi);
    const newUsers = filterNewUsers(existingData, selectedUsers);
    const newData = [...existingData, ...newUsers];

    setLocalUserData(newData);
    gridApi.setRowData(newData);
  }, [selectedUsers, gridApi]);

  useEffect(() => {
    if (isOpen) {
      if (selectedChannel) {
        setChannelName(selectedChannel.name);
        setIntroduceDesc(selectedChannel.description);
        setFixedStatus(selectedChannel.fixed);

        // API 요청
        const fetchCounselorData = async () => {
          try {
            const res = await counselCounselorListApi(selectedChannel.id);

            const counselorData = res.data.data.result.map((counselor: IAdminCounselCounselorRes) => ({
              bid: counselor.bid,
              userid: counselor.bid.split('.')[1],
              role: counselor.role,
              nickname: counselor.nickname || '',
              username: counselor.multiname[0].name,
            }));

            setLocalUserData(counselorData);
            if (gridApi) {
              gridApi.setRowData(counselorData);
            }
          } catch (error) {
            console.error('Failed to fetch counselor data:', error);
          }
        };

        fetchCounselorData();
      } else {
        setChannelName('');
        setIntroduceDesc('');
        setFixedStatus(0);
        setLocalUserData([]);
      }
    }
  }, [isOpen, selectedChannel, gridApi]);

  const hasAtLeastOneRole = () => {
    return localUserData.some((user) => Number(user.role) === 1);
  };
  const onConfirm = async () => {
    if (!selectedCategoryId) return toast.error(t('CounselingManagement.channelToast1') as string);
    if (!channelName) return toast.error(t('CounselingManagement.channelToast2') as string);
    if (!introduceDesc) return toast.error(t('CounselingManagement.channelToast3') as string);
    if (localUserData.length === 0) return toast.error(t('CounselingManagement.channelToast9') as string);

    if (!hasAtLeastOneRole()) return toast.error(t('CounselingManagement.channelToast10') as string);

    const data: IChannelv2 = {
      categoryid: selectedCategoryId,
      name: channelName,
      description: introduceDesc,
      fixed: fixedStatus,
    };

    try {
      const res = await reqAddChannel(data);
      console.log('res: ', res);
      if (res.data.data.result === true) {
        const counselorData: IAdminCounselCounselorReq = {
          who: localUserData.map((user) => ({
            bid: user.bid,
            nickname: user.nickname,
            channelid: res.data.data.channelid,
            role: Number(user.role),
          })),
        };

        const res2 = await reqAddCounselor(counselorData);
        console.log('res2: ', res2);
        if (res2.data.data.result === true) {
          toast.success(t('CounselingManagement.channelToast4') as string);
          dispatch(setSelectedUsers([]));
          dispatch(setChannelReloadStatus(true));
        } else {
          toast.error(t('CounselingManagement.channelToast5') as string);
        }
      }

      setChannelName('');
      setIntroduceDesc('');
      dispatch(setSelectedChannel(null));
      closeCounselingChannelModal();
    } catch (err) {
      console.error('Failed to save the channel: ', err);
      toast.error(t('CounselingManagement.channelToast6') as string);
    }
  };

  const onEdit = async () => {
    if (!selectedCategoryId) return toast.error(t('CounselingManagement.channelToast1') as string);
    if (!channelName) return toast.error(t('CounselingManagement.channelToast2') as string);
    if (!introduceDesc) return toast.error(t('CounselingManagement.channelToast3') as string);
    if (localUserData.length === 0) return toast.error(t('CounselingManagement.channelToast9') as string);
    if (!hasAtLeastOneRole()) return toast.error(t('CounselingManagement.channelToast10') as string);

    const data: IChannel[] = [
      {
        categoryid: selectedCategoryId,
        name: channelName,
        description: introduceDesc,
        fixed: fixedStatus,
        imagepath: selectedChannel?.imagepath,
        id: selectedChannel!.id,
      },
    ];

    const formattedData = {
      what: data.map((channel) => ({
        categoryid: channel.categoryid,
        name: channel.name,
        description: channel.description,
        imagepath: selectedChannel?.imagepath,
        fixed: channel.fixed,
        id: channel.id,
      })),
      counselor: localUserData.map((data) => ({
        bid: data.bid,
        nickname: data.nickname,
        role: Number(data.role),
      })),
    };

    try {
      const res = await reqModifyChannel(formattedData);
      console.log('modiRs: ', res);
      dispatch(setSelectedUsers([]));
      dispatch(setChannelReloadStatus(true));

      setChannelName('');
      setIntroduceDesc('');
      dispatch(setSelectedChannel(null));
      closeCounselingChannelModal();
    } catch (err) {
      console.error('Failed to save the channel: ', err);
      toast.error(t('CounselingManagement.channelToast6') as string);
    }
  };

  const applyThumbor = (imageUrl: string) => {
    console.log(imageUrl);
    const thumborServerUrl = 'https://thumbor.ucworks-dev.net/unsafe/';
    const size = '144x144';
    return `${thumborServerUrl}${size}/${encodeURIComponent(imageUrl)}`;
  };

  const applyThumborV2 = (imageUrl: string) => {
    const thumborServerUrl = 'https://thumbor.ucworks-dev.net/unsafe/';
    return `${thumborServerUrl}${encodeURIComponent(imageUrl)}`;
  };

  const onCancel = () => {
    dispatch(setSelectedUsers([]));
    dispatch(setSelectedChannel(null));
    closeCounselingChannelModal();
    setChannelName('');
    setIntroduceDesc('');
    setLocalUserData([]);
  };

  const radioOnChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setFixedStatus(Number(event.target.value));
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    const issue = await presignedURLIssue({
      sid: usersid ?? '',
      relkey: selectedChannel?.id ?? '',
      tenantid: getMyTenantId()!,
      operation: 'PUT',
      fileid: 'uuid',
    });

    const requestURL = issue.data.data.result[0].presignedurl;

    const imageUrlResponse = await presignedImageRequest(file, requestURL);
    const imageUrl = imageUrlResponse.data.data.result;
    // id: string;
    // name: string;
    // description: string;
    // fixed: number;
    // sortorder: number;
    // imagepath: string;
    // updatetime: string;
    if (selectedChannel) {
      dispatch(
        setSelectedChannel({
          id: selectedChannel.id,
          name: selectedChannel.name,
          description: selectedChannel.description,
          fixed: selectedChannel.fixed,
          sortorder: selectedChannel.sortorder,
          updatetime: selectedChannel.updatetime,
          imagepath: imageUrl,
        }),
      );
    }

    console.log(imageUrl);
  };
  return (
    <div>
      <Modal isOpen={isOpen} onRequestClose={onCancel} style={customStyles} contentLabel="Example Modal">
        <div
          className="flex flex-col h-full px-3
            relative border border-modal-subTitle bg-white z-50 shadow-listbox-options
            rounded
          "
        >
          <div className="min-h-[3.5rem] w-full flex flex-col relative text-center font-extrabold mt-5">
            {selectedChannel
              ? (t('CounselingManagement.channelMod') as string)
              : (t('CounselingManagement.channelAdd') as string)}
          </div>

          {/* 프로필 사진 */}
          <CounselingChannelsProfile />

          <div className=" w-full font-bold text-left text-[#9D9EA2] text-sm mt-3 mb-2">
            {t('CounselingManagement.channelName') as string}
            <span className="text-red-700">*</span>
          </div>

          <InputText
            value={channelName}
            handleChangeInput={inputHandler}
            handleClear={handleClear}
            placeholder={t('CounselingManagement.channelToast2') as string}
          />

          <div className=" w-full font-bold text-left text-[#9D9EA2] text-sm mt-6 mb-2">
            {t('CounselingManagement.channelIntro') as string}
            <span className="text-red-700">*</span>
          </div>
          <InputText
            value={introduceDesc}
            handleChangeInput={inputIntroduceHandler}
            handleClear={handleIntroduceClear}
            placeholder={t('CounselingManagement.channelToast3') as string}
          />

          <div className=" w-full font-bold text-left text-[#9D9EA2] text-sm mt-6 mb-2">
            {t('CounselingManagement.channelCounselor') as string}
            <span className="text-red-700">*</span>
          </div>
          <div className=" w-full font-normal text-left  text-[#9D9EA2] text-sm mb-2">
            {t('CounselingManagement.channelDesc') as string}
          </div>

          <div className="mt-1 mb-2">
            <button
              className="p-1.5 text-sm active:bg-C-b8d3fe text-C-1f62b9 border border-C-1f62b9 border-solid hover:bg-C-d9e5f9 w-14 rounded-sm mr-2"
              onClick={openCounselingCounselorModal}
            >
              {t('CounselingManagement.contextMenuAdd') as string}
            </button>
            <button
              className="p-1.5 text-sm active:bg-C-b8d3fe text-C-1f62b9 border border-C-1f62b9 border-solid hover:bg-C-d9e5f9 w-14 rounded-sm"
              onClick={handleDelete}
            >
              {t('CounselingManagement.contextMenuDel2') as string}
            </button>
          </div>

          <div className="ag-theme-alpine" style={{ height: 200, width: '100%' }}>
            <AgGridReact
              ref={gridRef}
              rowData={JSON.parse(JSON.stringify(localUserData))}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowMultiSelectWithClick
              rowSelection={'multiple'}
              // 변화가 생긴 Row 파라미터로 줌
              onCellValueChanged={onCellValueChanged}
              onGridReady={onGridReady}
              overlayNoRowsTemplate={'\xa0'}
            />
          </div>

          <div className=" w-full font-bold text-left text-[#9D9EA2] text-sm mt-6 mb-2">
            {t('CounselingManagement.channelFixed') as string}
            <span className="text-red-700"></span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <InputRadioCheckbox
              kind="radio"
              id="accept"
              value={'1'}
              name="federation"
              onChange={radioOnChangeHandler}
              checked={fixedStatus === 1}
            />
            <label className={`mr-5`} htmlFor={'accept'}>
              {t('CounselingManagement.channelFixed') as string}
            </label>
            <InputRadioCheckbox
              kind="radio"
              id="deny"
              value={'0'}
              name="federation"
              onChange={radioOnChangeHandler}
              checked={fixedStatus === 0}
            />
            <label htmlFor={'deny'}>{t('CounselingManagement.channelNotFixed') as string}</label>
          </div>
          <div className="min-h-[6rem] w-full jcac gap-2">
            <Button kind="cancel" label={t('ContextMenu.treeContextMenu.cancel')} onClick={onCancel} />
            <Button
              kind="primary"
              label={
                selectedChannel
                  ? (t('CounselingManagement.contextMenuMod') as string)
                  : (t('CounselingManagement.contextMenuAdd') as string)
              }
              onClick={() => {
                if (selectedChannel) {
                  onEdit();
                } else {
                  onConfirm();
                }
              }}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CounselingChannelModal;
